import { UnderlinedTokens } from "@/components/shared/UnderlinedTokens";
import type { SlideFields } from "@/types/Slide";
import { IoMdArrowBack } from "react-icons/io";

interface GrammarFormThumbnailProps {
  slide: SlideFields;
}

export function GrammarFormThumbnail({ slide }: GrammarFormThumbnailProps) {
  const formatKey = (key: string): string => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Only the 4 authored string fields belong in the thumbnail — skip __typename and the
  // grammarFocusNormalized/grammarFocusNeedsReview staging fields (the latter is a boolean).
  const AUTHORED_LIMIT_KEYS = ["grammarLimit", "grammarFocus", "wordLimit", "wordFocus"];
  const limitationEntries = slide.limitation
    ? Object.entries(slide.limitation).filter(
        ([key, value]) => AUTHORED_LIMIT_KEYS.includes(key) && typeof value === "string"
      )
    : [];

  return (
    <div className="bg-orange-50 h-full flex flex-col px-2 py-1.5 gap-1.5 overflow-hidden">
      {/* Header card */}
      <div className="bg-white rounded-lg overflow-hidden flex shrink-0 shadow-sm">
        <div className="bg-orange-400 flex items-center justify-center px-2 shrink-0">
          <span
            className="text-white font-black leading-none"
            style={{ fontSize: "7px", letterSpacing: "0.05em" }}
          >
            G
          </span>
        </div>
        <p className="text-[8px] font-semibold text-gray-700 leading-tight flex-1 px-2 py-1.5">
          {slide.name || "Grammar Form"}
        </p>
      </div>

      {/* Entries */}
      {limitationEntries.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-[9px] font-medium text-orange-300">No limitations set</span>
        </div>
      ) : (
        <div className="flex-1 flex flex-col gap-1 overflow-hidden">
          {limitationEntries.map(([key, value], index) => (
            <div key={key} className="bg-white rounded-lg overflow-hidden flex shadow-sm h-full">
              <div className="bg-orange-300 flex items-center justify-center px-1.5 shrink-0">
                <span
                  className="text-white font-black leading-none"
                  style={{ fontSize: "6px", letterSpacing: "0.05em", opacity: 0.9 }}
                >
                  {index + 1}
                </span>
              </div>
              <div className="flex flex-col px-2 py-2 min-w-0 overflow-hidden">
                <span className="text-[7px] font-bold text-orange-500 uppercase tracking-wide leading-none mb-0.5">
                  {formatKey(key)}
                </span>
                <p className="text-[8px] text-gray-600 leading-tight line-clamp-6">
                  {typeof value === "string" && value.trim() !== "" ? (
                    <UnderlinedTokens text={value} />
                  ) : (
                    "None"
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
